import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  IconButton,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { clientsAPI, documentsAPI } from '../services/api';

const DISCOUNT_TYPES = [
  { value: 'none', label: 'Aucune' },
  { value: 'percentage', label: 'Pourcentage (%)' },
  { value: 'fixed', label: 'Montant fixe (DZD)' },
];

const LINE_TYPES = [
  { value: 'installation', label: 'Frais d\'installation' },
  { value: 'subscription', label: 'Abonnement' },
  { value: 'service', label: 'Service' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'product', label: 'Produit' },
  { value: 'discount', label: 'Remise' },
  { value: 'note', label: 'Note' },
  { value: 'other', label: 'Autre' },
];

const emptyLineItem = {
  description: '',
  line_type: 'subscription',
  quantity: 1,
  months: 1,
  unit_price_ht: 0,
};

function ProformaCreationModal({ open, onClose, onSuccess }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState(() => {
    // Set default dates to next year (January 1st to December 31st)
    const nextYear = new Date().getFullYear() + 1;
    const firstDay = new Date(nextYear, 0, 1); // January 1st of next year
    const lastDay = new Date(nextYear, 11, 31); // December 31st of next year
    
    return {
      client: null,
      period_start: firstDay.toISOString().split('T')[0],
      period_end: lastDay.toISOString().split('T')[0],
      global_discount_type: 'none',
      global_discount_value: 0,
      global_vat_rate: 19,
      subscription_unit_price: 2500,
      include_renewal_line: true,
      notes: '',
    };
  });

  const [pdfTemplate, setPdfTemplate] = useState(null);


  // Line items for manual editing
  const [lineItems, setLineItems] = useState([
    {
      description: 'Abonnement plateforme gestion des flottes multi accès - 197.140.11.160:8007/',
      line_type: 'subscription',
      quantity: 0,
      months: 1,
      unit_price_ht: 2500,
      isCoreLine: true,
    },
    {
      description: 'Renouvellement',
      line_type: 'service',
      quantity: 1,
      months: 1,
      unit_price_ht: 0,
      isCoreLine: true,
    },
    {
      description: '',
      line_type: 'service',
      quantity: 1,
      months: 1,
      unit_price_ht: 0,
      isCoreLine: true,
    }
  ]);

  // Line item management functions
  const addLineItem = () => {
    setLineItems(prev => [
      ...prev.slice(0, 1),  // First item (subscription)
      { ...emptyLineItem, isCoreLine: false },   // New item inserted before renewal, marked as not core
      ...prev.slice(1)       // Rest of items (renewal, period, and any additional lines)
    ]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const removeLineItem = (index) => {
    // Only allow removing non-core lines
    if (lineItems[index]?.isCoreLine) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const moveUp = (index) => {
    if (index === 0) return; // Can't move up if already at top
    setLineItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
  };

  const moveDown = (index) => {
    if (index === lineItems.length - 1) return; // Can't move down if already at bottom
    setLineItems(prev => {
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotalHT = 0;
    let discountAmount = 0;

    // Calculate subtotal from line items
    lineItems.forEach(item => {
      const lineTotal = (item.quantity || 1) * (item.months || 1) * (item.unit_price_ht || 0);
      subtotalHT += lineTotal;
    });

    // Apply global discount
    if (formData.global_discount_type === 'percentage' && formData.global_discount_value > 0) {
      discountAmount = subtotalHT * (formData.global_discount_value / 100);
    } else if (formData.global_discount_type === 'fixed' && formData.global_discount_value > 0) {
      discountAmount = Math.min(formData.global_discount_value, subtotalHT);
    }

    const totalHT = subtotalHT - discountAmount;
    const totalVAT = totalHT * (formData.global_vat_rate / 100);
    const totalTTC = totalHT + totalVAT;

    return {
      subtotalHT,
      discountAmount,
      totalHT,
      totalVAT,
      totalTTC,
    };
  };

  const loadPdfTemplate = async () => {
    try {
      const response = await documentsAPI.getPdfTemplate('proforma');
      setPdfTemplate(response.data);
    } catch (err) {
      console.error('Error loading PDF template:', err);
      // Continue without template settings
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPdfTemplate();
      loadClients();
      // Reset form to clean state when modal opens
      const currentYear = new Date().getFullYear();
      const firstDay = new Date(currentYear,0, 2); // January 1st of current year
      const lastDay = new Date(currentYear, 11, 32); // December 31st of current year
      
      setFormData({
        client: null,
        period_start: firstDay.toISOString().slice(0, 10),
        period_end: lastDay.toISOString().slice(0, 10),
        global_discount_type: 'none',
        global_discount_value: 0,
        global_vat_rate: 19,
        subscription_unit_price: 2500,
        include_renewal_line: true,
        notes: '',
      });
      
      setLineItems([
        {
          ...emptyLineItem,
          description: pdfTemplate?.platform_url ? `Abonnement plateforme gestion des flottes multi accès - ${pdfTemplate.platform_url}` : 'Abonnement plateforme gestion des flottes multi accès',
          unit_price_ht: 2500,
          isCoreLine: true,
        },
        {
          ...emptyLineItem,
          description: 'Renouvellement',
          line_type: 'service',
          unit_price_ht: 0,
          isCoreLine: true,
        },
        {
          ...emptyLineItem,
          description: '',
          line_type: 'service',
          unit_price_ht: 0,
          isCoreLine: true,
        }
      ]);
    }
  }, [open, pdfTemplate?.platform_url]);

  // Calculate months from period dates
  const calculateMonthsFromPeriod = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate difference in months
    let months = (end.getFullYear() - start.getFullYear()) * 12 + 
               (end.getMonth() - start.getMonth()) + 1;
    
    // Ensure minimum of 1 month
    return Math.max(1, months);
  };

  // Update subscription line months when period changes
  useEffect(() => {
    if (formData.period_start && formData.period_end) {
      const months = calculateMonthsFromPeriod(formData.period_start, formData.period_end);
      
      setLineItems(prev => {
        const newItems = [...prev];
        if (newItems.length >= 1 && newItems[0].line_type === 'subscription') {
          newItems[0] = {
            ...newItems[0],
            months: months
          };
        }
        return newItems;
      });
    }
  }, [formData.period_start, formData.period_end]);

  // Check client devices when client changes and update renewal checkbox
  useEffect(() => {
    const checkClientDevices = async () => {
      if (formData.client && formData.period_start && formData.period_end) {
        try {
          const clientId = formData.client.id || formData.client;
          const response = await documentsAPI.getClientBillingData(
            clientId,
            formData.period_start,
            formData.period_end
          );
          
          const data = response.data;
          const activeDevices = data.devices || [];
          const deviceCount = activeDevices.length;
          
          // Calculate months from period
          const months = calculateMonthsFromPeriod(formData.period_start, formData.period_end);
          
          // Set renewal checkbox based on whether client has active devices
          setFormData(prev => ({ ...prev, include_renewal_line: deviceCount > 0 }));
          
          // Automatically create/update the main subscription line item
          const subscriptionItem = {
            description: formData.client?.server_url ? `Abonnement plateforme gestion des flottes multi accès - ${formData.client.server_url}` : 'Abonnement plateforme gestion des flottes multi accès',
            line_type: 'subscription',
            quantity: deviceCount,
            months: months,
            unit_price_ht: 2500,
          };
          
          const renewalDescription = deviceCount > 0 ? 'Renouvellement' : 'Nouvelle installation';
          const renewalItem = {
            description: renewalDescription,
            line_type: 'service',
            quantity: 1, // Keep default quantity
            months: 1,   // Keep default months
            unit_price_ht: 0,
          };
          
          // Format period dates for the period line
          const periodStartDate = new Date(formData.period_start);
          const periodEndDate = new Date(formData.period_end);
          const periodStartStr = periodStartDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const periodEndStr = periodEndDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const periodDescription = `Période ${periodStartStr} au ${periodEndStr}`;
          
          const periodItem = {
            description: periodDescription,
            line_type: 'service',
            quantity: 1, // Keep default quantity
            months: 1,   // Keep default months
            unit_price_ht: 0,
          };
          
          setLineItems([subscriptionItem, renewalItem, periodItem]);
          
        } catch (err) {
          console.error('Error checking client devices:', err);
          // Default to false if we can't check
          setFormData(prev => ({ ...prev, include_renewal_line: false }));
          setLineItems([{
            description: formData.client?.server_url ? `Abonnement plateforme gestion des flottes multi accès - ${formData.client.server_url}` : 'Abonnement plateforme gestion des flottes multi accès',
            line_type: 'subscription',
            quantity: 0,
            months: 1,
            unit_price_ht: 2500,
          },
          {
            description: 'Nouvelle installation',
            line_type: 'service',
            quantity: 1,
            months: 1,
            unit_price_ht: 0,
          },
          {
            description: 'Période à définir',
            line_type: 'service',
            quantity: 1,
            months: 1,
            unit_price_ht: 0,
          }]);
        }
      }
    };

    checkClientDevices();
  }, [formData.client, formData.period_start, formData.period_end]);

  // Update renewal line description when checkbox changes
  useEffect(() => {
    setLineItems(prev => {
      const newItems = [...prev];
      if (newItems.length >= 2) {
        newItems[1] = {
          ...newItems[1],
          description: formData.include_renewal_line ? 'Renouvellement' : 'Nouvelle installation'
        };
      }
      return newItems;
    });
  }, [formData.include_renewal_line]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.client) {
      setError('Veuillez sélectionner un client');
      return;
    }

    if (!formData.period_start || !formData.period_end) {
      setError('Veuillez définir la période de facturation');
      return;
    }

    if (lineItems.length === 0) {
      setError('Veuillez ajouter au moins une ligne');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        client: formData.client?.id || formData.client,
        period_start: formData.period_start,
        period_end: formData.period_end,
        global_discount_type: formData.global_discount_type,
        global_discount_value: parseFloat(formData.global_discount_value) || 0,
        global_vat_rate: parseFloat(formData.global_vat_rate) || 19,
        line_items: lineItems.map(item => ({
          description: item.description,
          line_type: item.line_type,
          quantity: item.quantity,
          months: item.months,
          unit_price_ht: item.unit_price_ht,
          discount_type: 'none',
          discount_value: 0,
          vat_rate: 0, // VAT is global
        })),
        include_renewal_line: formData.include_renewal_line,
        notes: formData.notes,
      };

      await documentsAPI.createProforma(payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creating proforma:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la création du proforma');
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Créer une Facture Proforma</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Client and Period Info */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} >
                <Grid item xs={12} md={4} sx={{ width: '500px' }}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => `${option.company_name} (${option.client_code})`}
                    value={formData.client}
                    onChange={(e, newValue) => handleFormChange('client', newValue)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <span>{option.company_name} ({option.client_code})</span>
                        {option.server_name && (
                          <span style={{ 
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2', 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            fontSize: '0.75em',
                          }}>
                            🔗 {option.server_name}
                          </span>
                        )}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" required multiline />
                    )}
                    componentsProps={{
                      paper: {
                        sx: { minWidth: 400 }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Période début"
                    type="date"
                    value={formData.period_start}
                    onChange={(e) => handleFormChange('period_start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Période fin"
                    type="date"
                    value={formData.period_end}
                    onChange={(e) => handleFormChange('period_end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Line Items */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Lignes de facturation</Typography>
              <Button startIcon={<AddIcon />} onClick={addLineItem} variant="outlined" size="small">
                Ajouter une ligne
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%"><strong>Description</strong></TableCell>
                    <TableCell width="12%"><strong>Type</strong></TableCell>
                    <TableCell width="11%" align="center"><strong>Qté</strong></TableCell>
                    <TableCell width="11%" align="center"><strong>Mois</strong></TableCell>
                    <TableCell width="13%" align="right"><strong>P.U. HT</strong></TableCell>
                    <TableCell width="15%" align="right"><strong>Total HT</strong></TableCell>
                    <TableCell width="5%" align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={1}
                          maxRows={4}
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          multiline
                          value={item.line_type}
                          onChange={(e) => updateLineItem(index, 'line_type', e.target.value)}
                        >
                          {LINE_TYPES.map(t => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        {item.line_type === 'note' ? (
                          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>--</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            sx={{ width: 140 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {item.line_type === 'note' ? (
                          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>--</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={item.months}
                            onChange={(e) => updateLineItem(index, 'months', parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            sx={{ width: 140 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {item.line_type === 'note' ? (
                          <Typography sx={{ textAlign: 'right', color: 'text.secondary' }}>--</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={item.unit_price_ht}
                            onChange={(e) => updateLineItem(index, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 100, style: { textAlign: 'right' } }}
                            sx={{ width: 180 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {item.line_type === 'note' ? (
                            <Typography sx={{ color: 'text.secondary' }}>-- DZD</Typography>
                          ) : (
                            <span>
                              {((item.quantity || 1) * (item.months || 1) * (item.unit_price_ht || 0)).toLocaleString('fr-FR')} DZD
                            </span>
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => moveUp(index)}
                            disabled={index === 0}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => moveDown(index)}
                            disabled={index === lineItems.length - 1}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                          {!item.isCoreLine ? (
                            <IconButton size="small" color="error" onClick={() => removeLineItem(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <div style={{ width: 24, height: 24 }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {lineItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">Aucune ligne</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Discount & Totals */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Remise Globale</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={formData.global_discount_type}
                        label="Type"
                        onChange={(e) => handleFormChange('global_discount_type', e.target.value)}
                      >
                        {DISCOUNT_TYPES.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {formData.global_discount_type !== 'none' && (
                      <TextField
                        size="small"
                        type="number"
                        label={formData.global_discount_type === 'percentage' ? 'Pourcentage' : 'Montant'}
                        value={formData.global_discount_value}
                        onChange={(e) => handleFormChange('global_discount_value', e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 120 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Sous-total HT:</Typography>
                    <Typography>{totals.subtotalHT.toLocaleString('fr-FR')} DZD</Typography>
                  </Box>
                  {totals.discountAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'error.main' }}>
                      <Typography>Remise:</Typography>
                      <Typography>-{totals.discountAmount.toLocaleString('fr-FR')} DZD</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Total HT:</Typography>
                    <Typography>{totals.totalHT.toLocaleString('fr-FR')} DZD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>TVA ({formData.global_vat_rate}%):</Typography>
                    <Typography>{totals.totalVAT.toLocaleString('fr-FR')} DZD</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total TTC:</Typography>
                    <Typography variant="h6" color="primary">
                      {totals.totalTTC.toLocaleString('fr-FR')} DZD
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Additional Options */}
            <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.include_renewal_line}
                      onChange={(e) => handleFormChange('include_renewal_line', e.target.checked)}
                    />
                  }
                  label="Renouvellement (coché) ou Nouvelle installation (non coché)"
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || lineItems.length === 0}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Création...' : 'Créer Proforma'}
        </Button>
      </DialogActions>

    </Dialog>
  );
}

export default ProformaCreationModal;
